import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FasteignService } from 'src/app/components/services/api/fasteign/fasteign.service';
import { NotandiService } from 'src/app/components/services/api/notandi/notandi.service';
import { ListPostnumerRes } from 'src/app/interfaces/responses/List/listPostnumerRes';
import { ListSvaediRes } from 'src/app/interfaces/responses/List/listSvaediRes';
import { AppService } from '../../app.service';

@Component({
  selector: 'app-properties-sold-search-home',
  templateUrl: './properties-sold-search-home.component.html',
  styleUrls: ['./properties-sold-search-home.component.scss'],

})
export class PropertiesSoldSearchHomeComponent implements OnInit {

  public isBrowser: boolean;
  public isServer: boolean;

  @Input() variant: number = 1;
  @Input() vertical: boolean = false;

  form: UntypedFormGroup;
  initialFormValue: UntypedFormGroup;

  // Listi yfir hluti sem hægt er að nota í filtering.
  // Sótt frá vefþjónustu.
  public postnumer: Array<ListPostnumerRes>;
  public svaedi: Array<ListSvaediRes>;

  verdOptions: Array<number> = [
    10,
    20,
    30,
    40,
    50,
    60,
    70,
    80,
    90,
    100,
    110,
    120,
    130,
    140,
    150,
    160,
    170,
    180,
    190,
    200,
    250,
    500
  ]

  public getAppearance() {
    return (this.variant != 3) ? 'outline' : '';
  }

  public getFloatLabel() {
    return (this.variant == 1) ? 'always' : '';
  }

  // --------------------------- INIT ------------------------------------------------------------
  constructor(public appService: AppService, public _fb: UntypedFormBuilder, public dialog: MatDialog, public fasteignService: FasteignService, @Inject(PLATFORM_ID) platformId: Object, private notandiService: NotandiService, private router: Router) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isServer = isPlatformServer(platformId);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.initForm();
      this.saekjaSvaedi();
    }
  }

  //#region Init functions

  initForm() {
    this.form = this._fb.group({
      searchText: null,
      region: this._fb.control([]),
      price: this._fb.group({
        from: null,
        to: null
      }),
      size: this._fb.group({
        from: null,
        to: null
      }),
      pageNumber: 1,
      pageSize: 9,
      tilSolu: true,
      kaupsamningar: true,
      kort: true,
      tilSoluEfasteignir: true,
      tilSoluMbl: true,
      order: null
    });
    this.initialFormValue = JSON.parse(JSON.stringify(this.form.value));
  }

  //#endregion

  // -----------------------------------------------------------------------------------------------------
  // -------------------------------- HTTP Köll ----------------------------------------------------------

  /**
   * Leit fyrir eignir sem eru til staðar undir Kaupsögunni.
   */
  public searchClickedSold() {
    try {
      const currentValue = this.form.value;
      const isChanged = JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);

      if (isChanged) {
        localStorage.setItem("_seldarFilterForm", JSON.stringify(currentValue));
      }
    } catch (err) {
      console.error('Villa við vistun forms:', err);
    }

    this.router.navigate(['/kaupsagan']);
  }

  /**
   * Leit fyrir eignir sem eru skráðar til sölu hjá e-fasteignum.
   */
  public searchClickedForSale() {
    try {
      const currentValue = this.form.value;
      const isChanged = JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);

      if (isChanged) {
        localStorage.setItem("_tilSoluFilterForm", JSON.stringify(currentValue));
      }
    } catch (err) {
      console.error('Villa við vistun forms:', err);
    }

    this.router.navigate(['/til-solu']);
  }

  //#region Svæði filter

  groupedPostnumer: Group[] = [];
  selectedIds = new Set<number>(); // geymir valin numer

  // --- Data sækja & hópun ---
  saekjaSvaedi() {
    this.fasteignService.listPostnumer().subscribe(res => {
      this.postnumer = res.filter(
        x =>
          x.tegund != 'Pósthólf' &&
          // Engar eignir eru til staðar undir þessum póstnúmerum (sjá staðfangalista):
          x.numer != 150 &&
          x.numer != 155
      ) ?? [];

      // Hópun (óbreytt)
      const map = new Map<string, ListPostnumerRes[]>();
      for (const p of this.postnumer) {
        if (!map.has(p.svaedi)) map.set(p.svaedi, []);
        map.get(p.svaedi)!.push(p);
      }
      this.groupedPostnumer = Array.from(map, ([svaedi, items]) => ({
        svaedi,
        items: items.sort((a, b) => (a.numer ?? 0) - (b.numer ?? 0)),
      }));

      // Halda selectedIds í samræmi við tiltæk póstnúmer
      const validIds = new Set(this.postnumer.map(p => p.numer));
      for (const id of Array.from(this.selectedIds)) {
        if (!validIds.has(id)) this.selectedIds.delete(id);
      }

      // LESA forstillt gildi úr formi (t.d. úr localStorage endurhleðslu)
      const initial = (this.form.get('region')?.value ?? []) as number[];
      if (Array.isArray(initial) && initial.length) {
        // setja aðeins þau sem eru til
        for (const id of initial) {
          if (validIds.has(id)) this.selectedIds.add(id);
        }
      }

      // Loks spegla yfir í formið (þannig formið og UI séu í sync)
      this.syncFormRegionFromSelectedIds();
    });
  }

  // --- Valdar færslur (fyrir label) ---
  get valinPostnumer(): ListPostnumerRes[] {
    const m = new Map(this.postnumer.map(p => [p.numer, p] as const));
    return Array.from(this.selectedIds)
      .map(id => m.get(id)!)
      .filter(Boolean);
  }

  // --- Label í input ---
  get displayLabel(): string {
    // Ef engin gögn hlaðin
    if (!this.postnumer?.length) return 'Allt landið';

    // Ef ekkert valið eða allt valið
    if (this.selectedIds.size === 0 || this.selectedIds.size === this.postnumer.length) {
      return 'Allt landið';
    }

    // Annars sýna valin póstnúmer
    const items = this.valinPostnumer.map(
      p => `${p.numer} - ${(p.heitiNf ?? p.heiti)}`
    );

    if (items.length <= 2) return items.join(', ');
    return `${items.slice(0, 2).join(', ')} +${items.length - 2}`;
  }

  private syncFormRegionFromSelectedIds() {
    const ids = this.selectedIdsArray; // Array<number>
    this.form.get('region')?.setValue(ids, { emitEvent: false });
  }

  // --- Hak á stökum póstnúmerum ---
  toggleItem(p: ListPostnumerRes, checked: boolean) {
    if (checked) this.selectedIds.add(p.numer);
    else this.selectedIds.delete(p.numer);
    this.syncFormRegionFromSelectedIds();
  }

  // --- Hak á heilu svæði (velja/afv elja allt) ---
  toggleGroup(group: Group, checked: boolean) {
    if (checked) {
      for (const p of group.items) this.selectedIds.add(p.numer);
    } else {
      for (const p of group.items) this.selectedIds.delete(p.numer);
    }
    this.syncFormRegionFromSelectedIds();
  }

  // --- Staða svæðis-haks (til að sýna checked/indeterminate á header) ---
  isGroupAllSelected(group: Group): boolean {
    if (!group.items.length) return false;
    return group.items.every(p => this.selectedIds.has(p.numer));
  }

  isGroupIndeterminate(group: Group): boolean {
    const any = group.items.some(p => this.selectedIds.has(p.numer));
    return any && !this.isGroupAllSelected(group);
  }

  // --- Chips toggling (ef þú ert með chips annars staðar) ---
  togglePostnumer(p: ListPostnumerRes) {
    if (this.selectedIds.has(p.numer)) this.selectedIds.delete(p.numer);
    else this.selectedIds.add(p.numer);
    this.syncFormRegionFromSelectedIds();
  }

  // --- Reactive Forms tenging ef þarf ---
  get selectedIdsArray(): number[] {
    return Array.from(this.selectedIds);
  }

  //#endregion

}

type Group = { svaedi: string; items: ListPostnumerRes[] };
